import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, AlertTriangle, ChevronRight } from 'lucide-react';

/**
 * WelcomeModal - First-run welcome experience for new users.
 * Shows only once per user, then never again.
 */
const WelcomeModal = (props) => {
  const isOpen = props.isOpen;
  const onClose = props.onClose;
  const userName = props.userName;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleStart = async () => {
    setLoading(true);
    // Parent component handles marking welcome as seen
    onClose();
    navigate('/phases/pre-flight');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="welcome-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-xl rounded-gc-card border shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-border-card)' }}
      >
        {/* Header */}
        <div className="relative p-8 border-b" style={{ borderColor: 'var(--gc-border-nav)' }}>
          <div className="flex items-center justify-center mb-5">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center border"
              style={{ background: 'var(--gc-current-row)', borderColor: 'var(--gc-cyan)' }}
            >
              <Plane className="w-8 h-8" style={{ color: 'var(--gc-cyan)' }} />
            </div>
          </div>
          <h1 className="font-barlow-condensed font-bold text-2xl text-center" style={{ color: 'var(--gc-text-main)' }}>
            Welcome to Flight Sim Academy
          </h1>
          {userName && (
            <p className="text-center mt-2 font-medium font-plex-mono text-sm tracking-[0.08em]" style={{ color: 'var(--gc-amber)' }}>
              {userName}
            </p>
          )}
        </div>

        {/* Content */}
        <div className="p-8 space-y-5">
          <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>
            Flight Sim Academy is a structured procedural flight phase workflow platform designed for the Fenix A320
            on Microsoft Flight Simulator.
          </p>

          <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>
            Inside the platform you will find a complete flight workflow broken down into guided phases —
            from Cold &amp; Dark preparation through to shutdown and securing the aircraft.
          </p>

          <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>
            This system helps pilots build consistent and repeatable procedures during their simulator sessions.
          </p>

          {/* Important Notice - unchanged */}
          <div className="rounded-gc-card border p-4" style={{ background: 'rgba(245,176,65,0.08)', borderColor: 'var(--gc-amber-border)' }}>
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--gc-amber)' }}>
              <AlertTriangle className="w-4 h-4" />
              Important Notice
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-muted)' }}>
              Flight Sim Academy is an educational simulation tool designed for use within Microsoft Flight Simulator.
              It is not intended for real-world aviation and must not be used for real aircraft operation or certification.
              Procedures are simplified and adapted for simulation purposes.
            </p>
          </div>
        </div>

        {/* Footer with CTA */}
        <div className="p-6 border-t" style={{ borderColor: 'var(--gc-border-nav)' }}>
          <button
            onClick={handleStart}
            disabled={loading}
            className="w-full h-14 rounded-gc-button font-barlow-condensed font-bold text-lg flex items-center justify-center gap-2"
            style={{ background: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }}
            data-testid="begin-exploring-btn"
          >
            {loading ? (
              'Loading...'
            ) : (
              <>
                Start with Pre-Flight
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
